import type { ErrorEvent } from '@sentry/browser';

const APP_BUNDLE_PATH = '/static/js/';

/**
 * Flags SyntaxErrors raised by scripts the native iOS miniapp host evaluates
 * inside the webview (MiniAppSDK bridge responses built by string
 * interpolation). WebKit reports these sourceless parse failures via
 * window.onerror with the page URL as the only "frame" and no stack, so they
 * are indistinguishable from app errors by message alone. Errors thrown by our
 * own code always carry at least one frame from the app bundles under
 * /static/js/.
 *
 * See SMB-926 (this filter), SMB-373 (original investigation in the legacy
 * service-booking app) and MBX-784 (native root cause + rollout).
 */
export const isInjectedScriptSyntaxError = (event: ErrorEvent): boolean => {
  const exception = event.exception?.values?.[0];

  if (exception?.type !== 'SyntaxError' || exception.mechanism?.handled) {
    return false;
  }

  const frames = exception.stacktrace?.frames ?? [];

  return !frames.some((frame) => frame.filename?.includes(APP_BUNDLE_PATH));
};

/**
 * Sentry `beforeSend` hook: drops known-noise events before they are reported.
 *
 * Only filters the native-injected SyntaxError noise. The legacy app also
 * dropped unhandled analytics-tracking 401s here, but that source was removed
 * when this app moved to @hqo/web-tracking v5 (SMB-927), which wraps the POST
 * in try/catch — so there is nothing to filter for it.
 */
export const dropSentryNoise = (event: ErrorEvent): ErrorEvent | null =>
  (isInjectedScriptSyntaxError(event) ? null : event);
